import { useState, useEffect, useMemo } from "react";
import type { GroupView, GroupTicketView, Member, ReplacementPreview, ReplaceMemberPayload } from "@/services/types";
import { groupService, memberService } from "@/services";
import { useToast } from "@/context/ToastContext";
import { Modal } from "@/components/base/Modal";
import { Button } from "@/components/base/Button";
import { TextField } from "@/components/base/TextField";
import { Select } from "@/components/base/Select";
import { Checkbox } from "@/components/base/Checkbox";
import { Alert } from "@/components/base/Alert";
import { Avatar } from "@/components/base/Avatar";
import { Badge } from "@/components/base/Badge";
import { CurrencyDisplay } from "@/components/currency/CurrencyDisplay";

interface ReplaceMemberModalProps {
  open: boolean;
  group: GroupView;
  ticket: GroupTicketView | null;
  onClose: () => void;
  onSuccess: () => void;
}

const PAYMENT_MODE_OPTIONS = [
  { value: "CASH", label: "Cash" },
  { value: "UPI", label: "UPI" },
  { value: "BANK_TRANSFER", label: "Bank Transfer (NEFT/IMPS)" },
  { value: "CHEQUE", label: "Cheque" },
];

export function ReplaceMemberModal({
  open,
  group,
  ticket,
  onClose,
  onSuccess,
}: ReplaceMemberModalProps) {
  const toast = useToast();

  const [preview, setPreview] = useState<ReplacementPreview | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [allMembers, setAllMembers] = useState<Member[]>([]);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedNewMember, setSelectedNewMember] = useState<Member | null>(null);

  // Form states
  const [takeoverAmount, setTakeoverAmount] = useState<string>("0");
  const [refundAmount, setRefundAmount] = useState<string>("0");
  const [paymentMode, setPaymentMode] = useState<"CASH" | "UPI" | "BANK_TRANSFER" | "CHEQUE">("CASH");
  const [referenceNo, setReferenceNo] = useState("");
  const [clearArrears, setClearArrears] = useState(true);
  const [remarks, setRemarks] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const groupMemberId = ticket?.groupMemberId;

  // Load preview and members list on open
  useEffect(() => {
    if (!open || !ticket || !groupMemberId) return;

    let active = true;
    setError(null);
    setSelectedNewMember(null);
    setSearchQuery("");
    setReferenceNo("");
    setRemarks("");
    setClearArrears(true);
    setPaymentMode("CASH");

    setLoadingPreview(true);
    groupService
      .getReplacementPreview(group.id, groupMemberId)
      .then((data) => {
        if (!active) return;
        setPreview(data);
        setTakeoverAmount(String(data.suggestedTakeoverAmount));
        setRefundAmount(String(data.suggestedTakeoverAmount));
      })
      .catch((err) => {
        if (!active) return;
        setError(err instanceof Error ? err.message : "Failed to load replacement details.");
      })
      .finally(() => {
        if (active) setLoadingPreview(false);
      });

    setLoadingMembers(true);
    memberService
      .listMembers()
      .then((members) => {
        if (!active) return;
        setAllMembers(members);
      })
      .catch(() => {})
      .finally(() => {
        if (active) setLoadingMembers(false);
      });

    return () => {
      active = false;
    };
  }, [open, ticket, groupMemberId, group.id]);

  // Current active members in this group cannot be selected as replacement
  const activeEnrolledMemberIds = useMemo(() => {
    return new Set(
      group.tickets
        .filter((t) => t.memberId && t.member)
        .map((t) => t.memberId)
    );
  }, [group.tickets]);

  // Filter available candidate members
  const candidateMembers = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return allMembers
      .filter((m) => {
        // Must not already be an active seat holder in this group
        if (activeEnrolledMemberIds.has(m.id)) return false;
        // Must not be blacklisted
        if (m.status === "blacklisted") return false;
        if (!q) return true;
        return (
          m.fullName.toLowerCase().includes(q) ||
          m.memberCode.toLowerCase().includes(q) ||
          m.primaryPhone.includes(q)
        );
      })
      .slice(0, 10);
  }, [allMembers, activeEnrolledMemberIds, searchQuery]);

  const handleSubmit = async () => {
    if (!selectedNewMember) {
      setError("Please select an incoming replacement member.");
      return;
    }
    if (!groupMemberId) {
      setError("Missing group member identifier.");
      return;
    }

    const takeoverNum = Number(takeoverAmount);
    if (isNaN(takeoverNum) || takeoverNum < 0) {
      setError("Please enter a valid takeover amount.");
      return;
    }

    const refundNum = refundAmount !== "" ? Number(refundAmount) : takeoverNum;
    if (isNaN(refundNum) || refundNum < 0) {
      setError("Please enter a valid refund amount.");
      return;
    }

    setSubmitting(true);
    setError(null);

    const payload: ReplaceMemberPayload = {
      newMemberId: selectedNewMember.id,
      paymentMode,
      takeoverAmount: takeoverNum,
      clearArrears,
      refundAmount: refundNum,
      referenceNo: referenceNo.trim() || undefined,
      remarks: remarks.trim() || undefined,
    };

    try {
      await groupService.replaceMember(group.id, groupMemberId, payload);
      toast.success(
        "Member replaced successfully",
        `Ticket #${ticket?.seat} transferred from ${ticket?.member?.fullName} to ${selectedNewMember.fullName}. Exiting account held.`
      );
      onClose();
      onSuccess();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to replace member.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!ticket || !ticket.member) return null;

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="lg"
      title={
        <div className="flex items-center gap-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary-100 text-primary-700">
            <i className="ri-user-shared-line text-lg" />
          </div>
          <div>
            <h2 className="text-base font-semibold text-foreground-900">
              Replace Member — Ticket #{ticket.seat}
            </h2>
            <p className="text-xs text-foreground-500">
              Transfer this seat to an incoming member while placing the exiting member on hold.
            </p>
          </div>
        </div>
      }
      footer={
        <div className="flex w-full items-center justify-between gap-3">
          <Button variant="outline" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!selectedNewMember || submitting || loadingPreview}
            icon={submitting ? <i className="ri-loader-4-line animate-spin" /> : <i className="ri-check-line" />}
          >
            {submitting ? "Processing Replacement…" : "Confirm Replacement"}
          </Button>
        </div>
      }
    >
      <div className="space-y-4 py-1">
        {error ? (
          <Alert variant="danger" title="Replacement Error" onClose={() => setError(null)}>
            {error}
          </Alert>
        ) : null}

        {/* Section 1: Exiting Member Details */}
        <div className="rounded-lg border border-background-200 bg-background-100/50 p-3.5">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-3">
              <Avatar name={ticket.member.fullName} size="md" />
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-foreground-900">{ticket.member.fullName}</span>
                  <Badge color="neutral" size="sm">Ticket #{ticket.seat}</Badge>
                  <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[11px] font-medium text-amber-800">
                    Exiting (To be Held)
                  </span>
                </div>
                <div className="mt-0.5 text-xs text-foreground-500">
                  {ticket.member.memberCode} · {ticket.member.primaryPhone}
                </div>
              </div>
            </div>

            {loadingPreview ? (
              <div className="text-xs text-foreground-400">Calculating stats…</div>
            ) : (
              <div className="flex items-center gap-4 text-right">
                <div>
                  <div className="text-[11px] text-foreground-500">Total Paid (Past)</div>
                  <div className="tnum font-semibold text-foreground-900">
                    <CurrencyDisplay amount={preview?.totalPaid ?? 0} />
                  </div>
                </div>
                <div>
                  <div className="text-[11px] text-foreground-500">Pending Dues</div>
                  <div className="tnum font-semibold">
                    <CurrencyDisplay
                      amount={preview?.pendingDues ?? ticket.outstanding}
                      tone={(preview?.pendingDues ?? ticket.outstanding) > 0 ? "danger" : "neutral"}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="mt-2.5 border-t border-background-200/80 pt-2 text-[12px] text-foreground-500 flex items-center gap-2">
            <i className="ri-information-line text-amber-600" />
            <span>
              The exiting member's record and ledger will be preserved in <strong>Hold Mode (REPLACED)</strong> and excluded from future auctions.
            </span>
          </div>
        </div>

        {/* Section 2: Choose Incoming Member */}
        <div className="space-y-2">
          <label className="block text-xs font-semibold text-foreground-700">
            Incoming Replacement Member <span className="text-danger-500">*</span>
          </label>

          {selectedNewMember ? (
            <div className="flex items-center justify-between rounded-lg border border-primary-200 bg-primary-50/50 p-3">
              <div className="flex items-center gap-3">
                <Avatar name={selectedNewMember.fullName} size="sm" />
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-foreground-900">{selectedNewMember.fullName}</span>
                    <Badge color="accent" size="sm">New Ticket Holder</Badge>
                  </div>
                  <div className="text-xs text-foreground-500">
                    {selectedNewMember.memberCode} · {selectedNewMember.primaryPhone}
                  </div>
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSelectedNewMember(null)}
                className="text-foreground-500 hover:text-danger-600"
              >
                <i className="ri-close-line mr-1" /> Change
              </Button>
            </div>
          ) : (
            <div className="space-y-2">
              <TextField
                placeholder="Search by candidate name, code, or phone…"
                leftIcon="ri-search-line"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                autoFocus
              />

              <div className="max-h-48 overflow-y-auto rounded-lg border border-background-200 divide-y divide-background-200 bg-background-50">
                {loadingMembers ? (
                  <div className="p-4 text-center text-xs text-foreground-400">Loading members…</div>
                ) : candidateMembers.length === 0 ? (
                  <div className="p-4 text-center text-xs text-foreground-400">
                    {searchQuery ? "No eligible members found matching your search." : "No available candidate members."}
                  </div>
                ) : (
                  candidateMembers.map((m) => (
                    <div
                      key={m.id}
                      className="flex items-center justify-between p-2.5 hover:bg-background-100 transition-colors"
                    >
                      <div className="flex items-center gap-2.5">
                        <Avatar name={m.fullName} size="sm" />
                        <div>
                          <div className="text-xs font-medium text-foreground-900">{m.fullName}</div>
                          <div className="text-[11px] text-foreground-500">
                            {m.memberCode} · {m.primaryPhone}
                          </div>
                        </div>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setSelectedNewMember(m)}
                      >
                        Select
                      </Button>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </div>

        {/* Section 3: Financial Settlement & Takeover */}
        <div className="rounded-lg border border-background-200 bg-background-50 p-3.5 space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-foreground-700">Financial Settlement & Seat Takeover</span>
            <span className="text-[11px] text-foreground-400">Rule 5: New member covers past installments</span>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <TextField
                label="Takeover Amount (Paid by New Member)"
                type="number"
                min="0"
                step="100"
                value={takeoverAmount}
                onChange={(e) => {
                  setTakeoverAmount(e.target.value);
                  // Default refund to match takeover
                  setRefundAmount(e.target.value);
                }}
                helper="Suggested: Total paid by previous member in past auctions."
                required
              />
            </div>

            <div>
              <Select
                label="Payment Mode"
                value={paymentMode}
                onChange={(val) => setPaymentMode(val as typeof paymentMode)}
                options={PAYMENT_MODE_OPTIONS}
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <TextField
                label="Reference / Transaction No."
                placeholder="Optional (e.g. UTR / Cheque #)"
                value={referenceNo}
                onChange={(e) => setReferenceNo(e.target.value)}
              />
            </div>

            <div>
              <TextField
                label="Refund to Exiting Member"
                type="number"
                min="0"
                step="100"
                value={refundAmount}
                onChange={(e) => setRefundAmount(e.target.value)}
                helper="Settlement amount refunded to exiting member."
              />
            </div>
          </div>

          {(preview?.pendingDues ?? 0) > 0 ? (
            <div className="pt-1">
              <Checkbox
                label={
                  <span className="text-xs">
                    Clear pending dues of{" "}
                    <strong><CurrencyDisplay amount={preview?.pendingDues ?? 0} /></strong> for the exiting member
                  </span>
                }
                checked={clearArrears}
                onChange={(e) => setClearArrears(e.target.checked)}
              />
            </div>
          ) : null}

          <div>
            <TextField
              label="Remarks / Note"
              placeholder="e.g. Replacement due to relocation or member request"
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
            />
          </div>
        </div>
      </div>
    </Modal>
  );
}
